import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, firstValueFrom, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ITag, TAG_STATUS} from '../models/tag.model';
import {ConversationApiService} from '@shared/services/api/conversation/conversation-api.service';
import {IResponseApi} from '@core/models';
import {AuthService} from '@core/authentication/auth.service';

/**
 * Đổi cấu trúc ở Cài đặt nhãn → đợi rồi mới tải lại: BE ghi ES không refresh, đọc ngay có thể ra dữ liệu
 * cũ (refresh mặc định của ES ~1s). Đồng thời gộp các lần gọi liên tiếp (cùng tab + DO gửi lại) thành 1 request.
 */
const TAG_RELOAD_DELAY_MS = 2000;

/**
 * Quản lý danh sách nhãn (tags) toàn cục cho module hội thoại.
 * Trách nhiệm:
 * - Tải danh sách tags từ API /business/tag/all
 * - Cache trong BehaviorSubject để các component subscribe
 * - Cung cấp helper lọc tag theo fanpageId
 *
 * Tương ứng bản cũ: conversation.tags + cache.get('allTags') + loadAllTags()
 */
@Injectable({providedIn: 'root'})
export class TagService implements OnDestroy {
  /** Stream danh sách tất cả tags — UI subscribe qua async pipe */
  private _tags$ = new BehaviorSubject<ITag[]>([]);
  public tags$ = this._tags$.asObservable();

  /** Mapping nhóm tag theo page (tương thích bản cũ tagPageGroupMapping) */
  private _pageGroupMapping: Record<string, string> | any[] = {};
  public get pageGroupMapping(): Record<string, string> | any[] {
    return this._pageGroupMapping;
  }

  /** Cờ đánh dấu đã tải lần đầu hay chưa — tránh gọi API trùng lặp */
  private _loaded = false;
  /** Promise lock chống concurrent calls (tránh gọi API 2 lần cùng lúc) */
  private _loadingPromise: Promise<void> | null = null;
  /** Hẹn giờ tải lại khi Cài đặt nhãn đổi cấu trúc (xem TAG_RELOAD_DELAY_MS) */
  private _reloadTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private api: ConversationApiService,
    private authService: AuthService,
  ) {
    // Khi auth thay đổi (chọn page, đổi business) → reset cache + tải lại tags
    this.authService.onAuthChanged$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.resetState();
      void this.loadAllTags();
    });
  }

  ngOnDestroy(): void {
    if (this._reloadTimer) clearTimeout(this._reloadTimer);
    this._destroy$.next();
    this._destroy$.complete();
  }

  /**
   * Tải tất cả tags từ server. Gọi 1 lần khi vào module conversation.
   * Logic tương đương bản cũ: loadAllTags() trong conversation.component.ts
   *
   * Response: { items: ITag[], pageGroupMapping?: any[] }
   * @param force Cài đặt nhãn vừa đổi → bỏ qua cache, tải lại
   */
  public async loadAllTags(force = false): Promise<void> {
    // Bước 1: Tránh gọi trùng nếu đã load
    if (!force && this._loaded && this._tags$.getValue().length > 0) return;

    // Bước 2: Chống concurrent calls — nếu đang loading thì join promise hiện tại.
    // force: lượt đang chạy có thể đã đọc dữ liệu trước khi đổi → chờ xong rồi tải lại.
    if (this._loadingPromise) {
      if (!force) return this._loadingPromise;
      await this._loadingPromise;
    }
    // Trong lúc chờ có thể một lượt tải mới đã bắt đầu (sau khi đổi) → dùng chung
    if (this._loadingPromise) return this._loadingPromise;

    this._loadingPromise = (async () => {
      try {
        // Bước 3: Gọi API lấy toàn bộ tags
        const res: IResponseApi = await firstValueFrom(this.api.getAllTags());

        // Bước 3: Kiểm tra response
        if (res?.code !== 1 || !res?.data) {
          console.warn('[TagService] API getAllTags trả về không thành công:', res?.messages);
          return;
        }

        // Bước 4: Parse response data
        const data = res.data as any;
        const items: ITag[] = data?.result || [];

        // Bước 5: Sắp xếp theo thứ tự order
        items.sort((a, b) => (a.order || 0) - (b.order || 0));

        // Bước 6: Lưu mapping trước khi phát danh sách tags để các computed
        // phụ thuộc tags đọc đúng scope group ngay ở lần render đầu.
        if (data?.pageGroupMapping) {
          this._pageGroupMapping = data.pageGroupMapping;
        }

        // Bước 7: Cập nhật state
        this._tags$.next(items);
        this._loaded = true;
      } catch (err) {
        console.error('[TagService] Lỗi tải danh sách tags:', err);
      } finally {
        this._loadingPromise = null;
      }
    })();

    return this._loadingPromise;
  }

  /**
   * Lấy snapshot danh sách tags hiện tại (đồng bộ).
   */
  public getCurrentTags(): ITag[] {
    return this._tags$.getValue();
  }

  /**
   * Tags được phép gắn mới tại một page. Theo đúng V1: page thuộc group thì
   * chỉ hiển thị tags của group đó; còn lại chỉ hiển thị tag trực tiếp của page.
   * Không dùng điều kiện groupId tồn tại vì điều đó làm tag của page khác lẫn
   * vào input.
   */
  public getActiveAssignableTags(pageId: string): ITag[] {
    if (!pageId) return [];
    const pageGroupId = this.getPageGroupId(pageId);

    return this._tags$.getValue().filter(tag => {
      if (tag.status !== TAG_STATUS.ACTIVE) return false;
      if (pageGroupId) return String(tag.groupId) === pageGroupId;
      return !tag.groupId && String(tag.fanpageId) === String(pageId);
    });
  }

  private getPageGroupId(pageId: string): string | null {
    const mapping = this._pageGroupMapping;
    if (!Array.isArray(mapping)) {
      const mappedGroupId = mapping[String(pageId)];
      return mappedGroupId != null ? String(mappedGroupId) : null;
    }

    const matchedItem = mapping.find(item => {
      if (!item || typeof item !== 'object') return false;
      const row = item as Record<string, unknown>;
      const mappedPageId = row['pageId'] ?? row['fanpageId'] ?? row['id'];
      return String(mappedPageId) === String(pageId);
    });
    if (!matchedItem || typeof matchedItem !== 'object') return null;
    const row = matchedItem as Record<string, unknown>;
    const mappedGroupId = row['groupId'] ?? row['tagGroupId'];
    return mappedGroupId != null ? String(mappedGroupId) : null;
  }

  /**
   * Lọc danh sách tags phù hợp với 1 hội thoại cụ thể.
   * Logic: tag.code phải nằm trong conversation.tagIds
   * VÀ (tag.fanpageId == conversation.fanpageId HOẶC tag có groupId)
   *
   * Tương ứng bản cũ:
   * *ngIf="c.tagIds.includes(tag.code) && (tag.fanpageId==c.fanpageId || tag?.groupId)"
   *
   * @param tagIds Mảng tag codes từ conversation.tagIds
   * @param fanpageId fanpageId của hội thoại
   * @returns Mảng ITag[] đã lọc + sorted
   */
  public getMatchedTags(tagIds: string[], fanpageId: string): ITag[] {
    // Validate đầu vào
    if (!tagIds?.length || !fanpageId) return [];

    const allTags = this._tags$.getValue();
    if (!allTags.length) return [];

    // Lọc tags phù hợp: code khớp + (cùng page hoặc tag nhóm)
    return allTags.filter(tag =>
      tagIds.includes(tag.code) &&
      (tag.fanpageId === fanpageId || !!tag.groupId)
    );
  }

  /**
   * Cập nhật 1 tag trong danh sách (khi nhận socket event).
   * Tương đương logic trong io.onTagAndQuickMessage() bản cũ.
   */
  public upsertTag(tag: ITag): void {
    const current = this._tags$.getValue();
    const idx = current.findIndex(t => t.id === tag.id);

    if (idx >= 0) {
      // Đã tồn tại → cập nhật
      current[idx] = {...current[idx], ...tag};
    } else {
      // Chưa có → thêm mới
      current.push(tag);
    }

    // Sắp xếp lại theo order
    current.sort((a, b) => (a.order || 0) - (b.order || 0));
    this._tags$.next([...current]);
  }

  /**
   * Xóa tags theo danh sách ID (khi nhận socket event remove).
   */
  public removeTags(ids: string[]): void {
    if (!ids?.length) return;
    const current = this._tags$.getValue();
    this._tags$.next(current.filter(t => !ids.includes(t.id)));
  }

  /**
   * Nhận tag:changed từ trang Cài đặt nhãn: DO event của page/business, hoặc Cài đặt gọi trực tiếp
   * ở cùng tab (không chắc DO gửi lại cho chính tab đã bắn).
   * Payload FE13 handleTagChanged: { type: 'tag', value?, idsRemove?, action?, tags? }.
   * Chưa tải nhãn lần nào thì bỏ qua — lần vào hội thoại sau tự tải mới.
   */
  public handleTagChanged(data: any): void {
    if (!data || data.type !== 'tag' || !this._loaded) return;

    // Đổi page của nhóm, xoá nhóm, sao chép nhãn: pageGroupMapping cũng đổi → tải lại toàn bộ
    if (data.action) {
      this.scheduleReload();
      return;
    }

    if (Array.isArray(data.idsRemove)) {
      this.removeTags(data.idsRemove.map(String));
      return;
    }

    const value = data.value;
    if (!value?.id) return;
    // Nhãn mới thiếu code (so khớp conversation.tagIds) thì phải tải lại; BE add trả code = id nên
    // thường thêm thẳng được, không phụ thuộc ES đã refresh hay chưa.
    if (!value.code && !this._tags$.getValue().some(tag => tag.id === String(value.id))) {
      this.scheduleReload();
      return;
    }
    // Giữ cả nhãn tạm ngưng như /business/tag/all; thanh nhãn chỉ cho gắn nhãn ACTIVE
    this.upsertTag({
      ...value,
      id: String(value.id),
      ...(value.order != null ? {order: Number(value.order)} : {}),
      ...(value.status != null ? {status: Number(value.status)} : {}),
    });
  }

  private scheduleReload(): void {
    if (this._reloadTimer) clearTimeout(this._reloadTimer);
    this._reloadTimer = setTimeout(() => {
      this._reloadTimer = null;
      void this.loadAllTags(true);
    }, TAG_RELOAD_DELAY_MS);
  }

  /**
   * Reset state — gọi khi đổi business hoặc logout.
   */
  public resetState(): void {
    this._tags$.next([]);
    this._pageGroupMapping = {};
    this._loaded = false;
  }
}
